/**
 * ReviewQueuePanel — the fast-path verification queue pinned at the top of
 * the results list. It shows ONLY the criteria still awaiting a human
 * decision (needs-review impact, or flagged requires_human_review, and not
 * yet human-verified) as dense rows with INLINE [Confirm]/[Edit] actions, so
 * a reviewer works the whole queue with consecutive clicks — no
 * click-scroll-click through the full criteria sections below.
 *
 * Each row: kind/number badge + the compact verdict ("NEEDS REVIEW ·
 * uncertain", or the AI verdict for merely-flagged rows) + a one-line clamp
 * of the criterion. The chevron expands the AI reasoning + citation chips IN
 * PLACE; a document citation opens the source viewer in the right pane —
 * evidence and actions both stay under the cursor. Rows leave the queue as
 * they are verified and the panel disappears when the queue drains (or after
 * sign-off, when the parent omits the actions).
 *
 * Confirm = one-click verify-agree (records human_result = the AI result via
 * the parent's onConfirm); Edit opens the parent's verify/override modal.
 */
import { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, ClipboardCheck, Loader2, Pencil, ThumbsUp } from 'lucide-react';
import { EligibilityCitation, EligibilityCriterionResult } from '../../types';
import { CitationChip, IMPACT_STYLES, effectiveResult, impactOf } from './CriterionRow';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

interface Props {
  results: EligibilityCriterionResult[];
  /** document_id → filename, for citation chip labels. */
  documentNames: Record<string, string>;
  onCitationClick: (citation: EligibilityCitation) => void;
  /** One-click verify-agree. The panel renders nothing without it. */
  onConfirm?: (r: EligibilityCriterionResult) => Promise<void>;
  onEdit?: (r: EligibilityCriterionResult) => void;
  /** Informational line (§3.5 staleness): shown inside the queue — and keeps a
   *  slim panel visible even when the verify queue itself has drained. */
  notice?: string | null;
}

/** Still awaiting a human decision: not verified, and either its effective
 *  impact says needs-review or the AI explicitly flagged it. */
export const awaitingReview = (r: EligibilityCriterionResult): boolean =>
  !r.human_verified
  && (impactOf(effectiveResult(r), r.criterion_kind).impact === 'needs_review' || !!r.requires_human_review);

function QueueRow({
  result,
  documentNames,
  onCitationClick,
  onConfirm,
  onEdit,
}: {
  result: EligibilityCriterionResult;
} & Omit<Props, 'results'>) {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const verdict = impactOf(result.result, result.criterion_kind);
  const style = IMPACT_STYLES[verdict.impact];
  const citations = Array.isArray(result.citations) ? result.citations : [];
  const hasEvidence = !!result.ai_reasoning || citations.length > 0;

  const confirm = async () => {
    if (!onConfirm || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm(result);
      // On success the refetch drops this row from the queue — no local reset.
    } catch (e: any) {
      setError(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <li className="px-3 py-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
          disabled={!hasEvidence}
          title={hasEvidence ? (expanded ? 'Hide the AI reasoning and sources' : 'Show the AI reasoning and sources') : 'No evidence recorded for this criterion'}
          className="shrink-0 rounded p-1 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-gray-700/50 dark:hover:text-gray-300"
        >
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            {result.criterion_number && (
              <span
                className={`inline-flex shrink-0 items-center rounded px-1.5 py-px text-[10px] font-bold ${
                  result.criterion_kind === 'exclusion'
                    ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
                    : 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300'
                }`}
              >
                {result.criterion_number}
              </span>
            )}
            <span className={`text-[10px] font-bold tracking-wide ${style.text}`}>
              {verdict.label}
              {verdict.sub && <span className="font-medium opacity-80"> · {verdict.sub}</span>}
              {!verdict.sub && result.requires_human_review && (
                <span className="font-medium opacity-80"> · flagged by the AI</span>
              )}
            </span>
          </div>
          <p className="mt-0.5 line-clamp-1 break-words text-xs text-gray-700 dark:text-gray-300">
            {result.criterion_text || result.plain_language || '(criterion text unavailable)'}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {onConfirm && (
            <button
              type="button"
              onClick={confirm}
              disabled={busy}
              title="Agree with the AI verdict — records your verification with the same result"
              className="inline-flex min-h-[44px] items-center gap-1 rounded-lg border border-green-200 bg-white px-2 py-1 text-xs font-medium text-green-700 transition-colors duration-150 hover:bg-green-50 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-green-800 dark:bg-gray-800 dark:text-green-400 dark:hover:bg-green-900/20 sm:min-h-0"
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <ThumbsUp size={12} />}
              Confirm
            </button>
          )}
          {onEdit && (
            <button
              type="button"
              onClick={() => onEdit(result)}
              title="Record your own determination (override requires a reason)"
              className="inline-flex min-h-[44px] items-center gap-1 rounded-lg border border-primary-200 bg-white px-2 py-1 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-primary-800 dark:bg-gray-800 dark:text-primary-300 dark:hover:bg-primary-900/20 sm:min-h-0"
            >
              <Pencil size={12} />
              Edit
            </button>
          )}
        </div>
      </div>

      {error && (
        <div
          role="alert"
          className="ml-7 mt-1.5 break-words rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400"
        >
          {error}
        </div>
      )}

      {expanded && (
        <div className="ml-7 mt-1.5 border-l-2 border-amber-200 pl-3 dark:border-amber-800/60">
          <p className="break-words text-xs text-gray-700 dark:text-gray-300">
            {result.criterion_text || '(criterion text unavailable)'}
          </p>
          {result.ai_reasoning && (
            <p className="mt-1.5 whitespace-pre-wrap break-words text-xs leading-relaxed text-gray-500 dark:text-gray-400">
              {result.ai_reasoning}
            </p>
          )}
          {citations.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {citations.map((c, i) => (
                <CitationChip key={i} citation={c} documentNames={documentNames} onClick={() => onCitationClick(c)} />
              ))}
            </div>
          )}
        </div>
      )}
    </li>
  );
}

export default function ReviewQueuePanel({
  results,
  documentNames,
  onCitationClick,
  onConfirm,
  onEdit,
  notice = null,
}: Props) {
  const queue = useMemo(() => {
    const rows = results.filter(awaitingReview);
    // Protocol order: inclusions first, then exclusions, numeric inside each.
    const rank = (r: EligibilityCriterionResult) => (r.criterion_kind === 'exclusion' ? 1 : 0);
    return rows.sort((a, b) =>
      rank(a) - rank(b)
      || (a.criterion_number || '').localeCompare(b.criterion_number || '', undefined, { numeric: true }));
  }, [results]);

  // No actions (e.g. after sign-off) or nothing left to review — no panel,
  // unless a notice (§3.5 staleness) still needs a home.
  const showQueue = !!onConfirm && queue.length > 0;
  if (!showQueue && !notice) return null;

  return (
    <section className="overflow-hidden rounded-xl border border-amber-200 bg-white shadow-sm dark:border-amber-800/60 dark:bg-gray-800">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b border-amber-100 bg-amber-50/70 px-3 py-2 dark:border-amber-900/40 dark:bg-amber-900/15">
        <h2 className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-900 dark:text-amber-300">
          <ClipboardCheck size={15} />
          Needs your review
          {showQueue && (
            <span className="rounded-full bg-amber-200/80 px-1.5 py-px text-[11px] font-bold text-amber-900 dark:bg-amber-800/60 dark:text-amber-200">
              {queue.length}
            </span>
          )}
        </h2>
        {showQueue && (
          <p className="text-[11px] text-amber-800/80 dark:text-amber-300/70">
            Confirm agrees with the AI · Edit records your own result · sources open on the right
          </p>
        )}
      </div>
      {notice && (
        <p className="border-b border-amber-100 bg-amber-50/40 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-900/10 dark:text-amber-300">
          {notice}
        </p>
      )}
      {showQueue && (
        <ul className="divide-y divide-gray-100 dark:divide-gray-700/60">
          {queue.map((r) => (
            <QueueRow
              key={r.id}
              result={r}
              documentNames={documentNames}
              onCitationClick={onCitationClick}
              onConfirm={onConfirm}
              onEdit={onEdit}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
